import { useState, useEffect } from 'react';
import { Plus, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { GOLD, fmt, Stat, Field, Card } from './shared';

export const ChangeOrdersTab = ({ api, jobId, readOnly }) => {
  const [data, setData] = useState({ orders: [], summary: {} });
  const reload = () => { if (jobId) api.get(`/accounting/jobs/${jobId}/change-orders`).then(r => setData(r.data)).catch(() => {}); };
  useEffect(reload, [api, jobId]);
  const [form, setForm] = useState({ description: '', amount: 0, reason: '' });
  const add = async () => {
    if (!form.description) return toast.error('Description required');
    try {
      await api.post(`/accounting/jobs/${jobId}/change-orders`, { job_id: jobId, ...form });
      setForm({ description: '', amount: 0, reason: '' });
      reload(); toast.success('Change order created');
    } catch { toast.error('Failed'); }
  };
  const approve = async (id) => {
    try { await api.patch(`/accounting/jobs/${jobId}/change-orders/${id}/approve`); reload(); toast.success('Approved'); }
    catch { toast.error('Failed'); }
  };
  if (!jobId) return <p className="text-gray-500">Select a project.</p>;
  return (
    <div data-testid="fos-change-orders-tab">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Stat label="Approved" value={fmt(data.summary?.approved_total)} accent="#16a34a" testid="co-approved" />
        <Stat label="Pending" value={fmt(data.summary?.pending_total)} accent="#d97706" testid="co-pending" />
        <Stat label="Total" value={fmt(data.summary?.total)} accent={GOLD} />
      </div>
      {!readOnly && (
        <Card className="mb-6">
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <Field label="Description" type="text" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="co-desc" />
            <Field label="Amount" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="co-amount" />
            <Field label="Reason" type="text" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} testid="co-reason" />
            <button data-testid="co-add-btn" onClick={add} className="h-10 inline-flex items-center justify-center gap-2 px-4 bg-[#C6A87C] text-white text-sm font-medium rounded-lg hover:opacity-90">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
        </Card>
      )}
      <Card>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-400 border-b border-gray-200">
            <th className="py-2">CO #</th><th>Description</th><th>Amount</th><th>Status</th>{!readOnly && <th></th>}
          </tr></thead>
          <tbody data-testid="co-table">
            {(data.orders || []).length === 0 && <tr><td colSpan="5" className="py-4 text-gray-400">No change orders.</td></tr>}
            {(data.orders || []).map(o => (
              <tr key={o.id} className="border-b border-gray-100">
                <td className="py-2 font-mono text-xs">{o.order_number}</td>
                <td>{o.description}</td>
                <td>{fmt(o.amount)}</td>
                <td>
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${o.approved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {o.approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {o.status}
                  </span>
                </td>
                {!readOnly && <td>{!o.approved && <button data-testid={`co-approve-${o.id}`} onClick={() => approve(o.id)} className="text-xs text-[#C6A87C] font-medium hover:underline">Approve</button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
